import React, { useState } from "react";
import { toast } from "sonner";
import { Send, Github, Linkedin, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { SectionHeader } from "./About";

export default function Contact({ profile }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      if (data.ok) {
        toast.success(data.sent ? "Message sent. Talk soon." : "Message stored. I'll be in touch.");
        setForm({ name: "", email: "", subject: "", message: "" });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <SectionHeader number="10" overline="Contact" title="Open to multi-agentic ideas, roles, and wild experiments." />
        <div className="mt-14 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="text-fog-200 leading-relaxed">
              Whether you're building an agentic finance platform, scaling RAG for a real product, or hiring a senior ML/MLOps mind — I'd love to talk.
            </p>
            <div className="mt-8 space-y-3">
              <a
                href={`mailto:${profile?.email || "edu.sidharth@gmail.com"}`}
                className="group flex items-center gap-3 text-fog-100 hover:text-volt"
                data-testid="contact-email-link"
              >
                <Mail className="w-5 h-5" />
                <span className="text-sm break-all">{profile?.email || "edu.sidharth@gmail.com"}</span>
              </a>
              <a href={profile?.linkedin || "https://linkedin.com/in/sidharthkumarpradhan"} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-fog-100 hover:text-volt">
                <Linkedin className="w-5 h-5" /> <span className="text-sm">linkedin.com/in/sidharthkumarpradhan</span>
              </a>
              <a href={profile?.github || "https://github.com/sidharthkumarpradhan"} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-fog-100 hover:text-volt">
                <Github className="w-5 h-5" /> <span className="text-sm">github.com/sidharthkumarpradhan</span>
              </a>
            </div>
          </div>

          <form onSubmit={submit} className="md:col-span-7 space-y-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="overline text-fog-400 block mb-1">Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-input-name" />
              </div>
              <div>
                <label className="overline text-fog-400 block mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-input-email" />
              </div>
            </div>
            <div>
              <label className="overline text-fog-400 block mb-1">Subject</label>
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-input-subject" />
            </div>
            <div>
              <label className="overline text-fog-400 block mb-1">Message</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-input-message"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-volt disabled:opacity-60" data-testid="contact-submit-btn">
              <Send className="w-4 h-4" /> {loading ? "Transmitting..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
